import type { QueryFunction, QueryKey } from '@tanstack/react-query'

import type {
  ServiceGetAllResponse,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { AlsintanDataType } from '../daftar-poktan/types'
import type {
  CommonReferenceType,
  GolonganType,
  JenisKelembagaanType,
  JenisPetaniType,
  KelasPoktanType,
  KomoditasType,
  StatusPetaniType,
  WilayahType
} from './types'

const REFERENCES_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['references', key] as const

interface InfiniteQueryOptions<T> {
  queryKey: QueryKey
  initialPageParam: number
  getNextPageParam: (lastPage: ServiceGetAllResponse<T>) => number | undefined
  queryFn: QueryFunction<ServiceGetAllResponse<T>, QueryKey, number>
}

const formatWilayahParams = (params?: Record<string, any>) => {
  if (!params) return { queryParams: {}, identifier: '' }
  const { identifier, ...rest } = params
  const queryParams: Record<string, any> = {}

  for (const [key, value] of Object.entries(rest)) {
    if (Array.isArray(value)) {
      const filtered = value.filter(Boolean)
      if (filtered.length > 0) {
        queryParams[key] = filtered.join(',')
      }
    } else if (value !== undefined && value !== null && value !== '') {
      queryParams[key] = value
    }
  }

  return {
    identifier: identifier ?? '',
    queryParams
  }
}

const getAllWilayahInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<WilayahType[]> => {
  const { identifier, queryParams } = formatWilayahParams(params)
  return {
    queryKey: REFERENCES_QUERY_KEY({ ...queryParams, identifier, type: 'wilayah' }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ServiceGetAllResponse<WilayahType[]>) => {
      const meta = lastPage.meta

      if (!meta) return undefined
      return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
        ? (meta?.current_page ?? 0) + 1
        : undefined
    },
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      try {
        const response = await api.get<ServiceGetAllResponse<WilayahType[]>>(
          `/referensi/wilayah-indonesia${identifier}`,
          {
            params: {
              ...queryParams,
              page: pageParam,
              per_page: queryParams?.per_page ?? 50
            }
          }
        )

        return {
          message: response.data.message,
          meta: response.data.meta,
          data: response.data.data
        }
      } catch (error) {
        throw handleAxiosError(error)
      }
    }
  }
}

const getAllWilayah = (
  params?: Record<string, any>
): ServiceQueryOptionType<ServiceGetAllResponse<Array<WilayahType>>> => {
  const { identifier, queryParams } = formatWilayahParams(params)
  return {
    queryKey: REFERENCES_QUERY_KEY({ ...queryParams, identifier, type: 'wilayah-query' }),
    queryFn: async () => {
      try {
        const response = await api.get<ServiceGetAllResponse<Array<WilayahType>>>(
          `/referensi/wilayah-indonesia${identifier}`,
          {
            params: {
              ...queryParams,
              page: 1,
              per_page: 50
            }
          }
        )

        return response.data
      } catch (error) {
        throw handleAxiosError(error)
      }
    }
  }
}

const getAllJenisKelamin = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'jenis_kelamin' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/jenis-kelamin', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllJenisPetani = (
  params?: Record<string, any>
): ServiceQueryOptionType<ServiceGetAllResponse<Array<JenisPetaniType>>> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'jenis_petani' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<JenisPetaniType>>
      >('/referensi/jenis-petani', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllKomoditasInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<KomoditasType[]> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'komoditas' }),
  initialPageParam: 1,
  getNextPageParam: (lastPage: ServiceGetAllResponse<KomoditasType[]>) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<ServiceGetAllResponse<KomoditasType[]>>(
        `/referensi/komoditas`,
        {
          params: {
            ...params,
            page: pageParam
          }
        }
      )

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllKelasPoktanInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<KelasPoktanType[]> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'kelas-poktan-infinite' }),
  initialPageParam: 1,
  getNextPageParam: (lastPage: ServiceGetAllResponse<KelasPoktanType[]>) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<ServiceGetAllResponse<KelasPoktanType[]>>(
        `/referensi/kelas-poktan`,
        {
          params: {
            ...params,
            page: pageParam
          }
        }
      )

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllKelasPoktan = (
  params?: Record<string, any>
): ServiceQueryOptionType<ServiceGetAllResponse<Array<KelasPoktanType>>> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'kelas-poktan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<KelasPoktanType>>
      >('/referensi/kelas-poktan', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllSubsektor = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'subsektor' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/subsektor', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllSubsektorInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<CommonReferenceType[]> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'subsektor-infinite' }),
  initialPageParam: 1,
  getNextPageParam: (
    lastPage: ServiceGetAllResponse<CommonReferenceType[]>
  ) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<CommonReferenceType[]>
      >(`/referensi/subsektor`, {
        params: {
          ...params,
          page: pageParam
        }
      })

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllJenisKelompokPoktanInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<CommonReferenceType[]> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'jenis-poktan' }),
  initialPageParam: 1,
  getNextPageParam: (
    lastPage: ServiceGetAllResponse<CommonReferenceType[]>
  ) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<CommonReferenceType[]>
      >(`/referensi/jenis-kelompok-poktan`, {
        params: {
          ...params,
          page: pageParam
        }
      })

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllJenisPoktanAnggota = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'jenis-poktan-anggota' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/jenis-anggota-poktan', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllUnitUsaha = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'unit-usaha' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/unit-usaha', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllUsahaOlah = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'usaha-olah' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/usaha-olah', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllAlsintanInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<AlsintanDataType[]> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'alsintan' }),
  initialPageParam: 1,
  getNextPageParam: (lastPage: ServiceGetAllResponse<AlsintanDataType[]>) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<ServiceGetAllResponse<AlsintanDataType[]>>(
        `/referensi/alsintan`,
        {
          params: {
            ...params,
            page: pageParam
          }
        }
      )

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllJenisKelembagaan = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<JenisKelembagaanType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'jenis_kelembagaan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<JenisKelembagaanType>>
      >('/referensi/bentuk-kelembagaan-bpp', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllJenjangPendidikan = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'jenjang-pendidikan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/jenjang-pendidikan', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllAgama = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'agama' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/agama', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllStatusPernikahan = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'status-pernikahan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/status-pernikahan', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllStatusPenyuluh = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'status-penyuluh' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/status-penyuluh', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllBidangUsaha = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'bidang-usaha' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/bidang-usaha', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllSarprasInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<CommonReferenceType[]> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'sarana-prasarana' }),
  initialPageParam: 1,
  getNextPageParam: (
    lastPage: ServiceGetAllResponse<CommonReferenceType[]>
  ) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<CommonReferenceType[]>
      >(`/referensi/sarana-prasarana`, {
        params: {
          ...params,
          page: pageParam
        }
      })

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllJenisPembiayaanInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<CommonReferenceType[]> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'jenis-pembiayaan' }),
  initialPageParam: 1,
  getNextPageParam: (
    lastPage: ServiceGetAllResponse<CommonReferenceType[]>
  ) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<CommonReferenceType[]>
      >(`/referensi/jenis-pembiayaan`, {
        params: {
          ...params,
          page: pageParam
        }
      })

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllPotensiEkonomiInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<CommonReferenceType[]> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'potensi-ekonomi' }),
  initialPageParam: 1,
  getNextPageParam: (
    lastPage: ServiceGetAllResponse<CommonReferenceType[]>
  ) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<CommonReferenceType[]>
      >(`/referensi/potensi-ekonomi`, {
        params: {
          ...params,
          page: pageParam
        }
      })

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllLahanPercontohanInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<CommonReferenceType[]> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'lahan-percontohan' }),
  initialPageParam: 1,
  getNextPageParam: (
    lastPage: ServiceGetAllResponse<CommonReferenceType[]>
  ) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<CommonReferenceType[]>
      >(`/referensi/lahan-percontohan`, {
        params: {
          ...params,
          page: pageParam
        }
      })

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllFasilitasBppInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<CommonReferenceType[]> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'fasilitasi-bpp' }),
  initialPageParam: 1,
  getNextPageParam: (
    lastPage: ServiceGetAllResponse<CommonReferenceType[]>
  ) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<CommonReferenceType[]>
      >(`/referensi/fasilitasi-bpp`, {
        params: {
          ...params,
          page: pageParam
        }
      })

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllKlasifikasiBppInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<CommonReferenceType[]> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'klasifikasi-bpp' }),
  initialPageParam: 1,
  getNextPageParam: (
    lastPage: ServiceGetAllResponse<CommonReferenceType[]>
  ) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<CommonReferenceType[]>
      >(`/referensi/klasifikasi-bpp`, {
        params: {
          ...params,
          page: pageParam
        }
      })

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllJabatanPenyuluh = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'jabatan-penyuluh' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/jabatan-penyuluh', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllGolonganPenyuluh = (
  params?: Record<string, any>
): ServiceQueryOptionType<ServiceGetAllResponse<Array<GolonganType>>> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'golongan-penyuluh' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<GolonganType>>
      >('/referensi/golongan-penyuluh', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllJenisKelembagaanKep = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'jenis-kelembagaan-kep' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/jenis-kep', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllJenisKepInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<CommonReferenceType[]> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'jenis-kep' }),
  initialPageParam: 1,
  getNextPageParam: (
    lastPage: ServiceGetAllResponse<CommonReferenceType[]>
  ) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<CommonReferenceType[]>
      >(`/referensi/jenis-kep`, {
        params: {
          ...params,
          page: pageParam
        }
      })

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllJenisMitraKepInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<CommonReferenceType[]> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'jenis-mitra-kep' }),
  initialPageParam: 1,
  getNextPageParam: (
    lastPage: ServiceGetAllResponse<CommonReferenceType[]>
  ) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<CommonReferenceType[]>
      >(`/referensi/jenis-mitra-kep`, {
        params: {
          ...params,
          page: pageParam
        }
      })

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllBentukMitraKep = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'bentuk-mitra-kep' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/bentuk-mitra-kep', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllRangeAge = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'rentang-usia' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/rentang-usia', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllStatusSubmission = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({
    ...params,
    type: 'status-ajuan-verifikasi'
  }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/status-ajuan-verifikasi', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllTypeSubmission = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({
    ...params,
    type: 'jenis-ajuan-verifikasi'
  }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/jenis-ajuan-verifikasi', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllBank = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'bank' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/bank', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllJenisDokumenPembentukan = (
  params?: Record<string, any>
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<CommonReferenceType>>
> => ({
  queryKey: REFERENCES_QUERY_KEY({
    ...params,
    type: 'jenis-dokumen-pembentukan'
  }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<CommonReferenceType>>
      >('/referensi/jenis-dokumen-pembentukan', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllStatusPetani = (
  params?: Record<string, any>
): ServiceQueryOptionType<ServiceGetAllResponse<Array<StatusPetaniType>>> => ({
  queryKey: REFERENCES_QUERY_KEY({ ...params, type: 'status-petani' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<StatusPetaniType>>
      >('/referensi/status-petani', {
        params: {
          ...params,
          page: 1,
          per_page: 50
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const referensi = {
  getAllWilayahInfinite,
  getAllWilayah,
  getAllJenisKelamin,
  getAllJenisPetani,
  getAllKomoditasInfinite,
  getAllKelasPoktanInfinite,
  getAllKelasPoktan,
  getAllSubsektor,
  getAllSubsektorInfinite,
  getAllJenisKelompokPoktanInfinite,
  getAllJenisPoktanAnggota,
  getAllUnitUsaha,
  getAllUsahaOlah,
  getAllAlsintanInfinite,
  getAllJenisKelembagaan,
  getAllJenjangPendidikan,
  getAllAgama,
  getAllStatusPernikahan,
  getAllBidangUsaha,
  getAllSarprasInfinite,
  getAllJenisPembiayaanInfinite,
  getAllPotensiEkonomiInfinite,
  getAllLahanPercontohanInfinite,
  getAllFasilitasBppInfinite,
  getAllKlasifikasiBppInfinite,
  getAllJabatanPenyuluh,
  getAllGolonganPenyuluh,
  getAllJenisKepInfinite,
  getAllJenisMitraKepInfinite,
  getAllBentukMitraKep,
  getAllStatusPenyuluh,
  getAllJenisKelembagaanKep,
  getAllRangeAge,
  getAllStatusSubmission,
  getAllTypeSubmission,
  getAllBank,
  getAllJenisDokumenPembentukan,
  getAllStatusPetani
}
